import { motion } from 'framer-motion';
import { TrendingUp, TrendingDown, Minus, Droplets, AlertCircle } from 'lucide-react';
import { useMemo } from 'react';

interface TopProducersProps {
    wells: any[];
    isDarkMode: boolean;
    onSelectWell: (well: any) => void;
    /** Optional calculated production results map (wellId -> production) */
    calculatedProduction?: Map<string, {
        netOilBBL: number;
        grossWaterBBL: number;
        waterCutPercent: number;
        oilVsYesterdayPercent?: number;
    }>;
    /** Show calculated vs raw values */
    useCalculatedValues?: boolean;
}

export function TopProducers({ 
    wells, 
    isDarkMode, 
    onSelectWell,
    calculatedProduction,
    useCalculatedValues = false
}: TopProducersProps) {
    // Sort wells by production (calculated or raw)
    const sortedWells = useMemo(() => {
        return [...wells]
            .map(well => {
                const calc = calculatedProduction?.get(well.id);
                return {
                    ...well,
                    displayProduction: useCalculatedValues && calc 
                        ? calc.netOilBBL 
                        : (well.oilProduction || 0),
                    waterCut: calc?.waterCutPercent || well.waterCutPercent || 0,
                    trend: calc?.oilVsYesterdayPercent,
                    isCalculated: useCalculatedValues && !!calc,
                };
            })
            .filter(w => w.displayProduction > 0)
            .sort((a, b) => b.displayProduction - a.displayProduction)
            .slice(0, 5);
    }, [wells, calculatedProduction, useCalculatedValues]);

    const getTrendIcon = (trend?: number) => {
        if (trend === undefined) return null;
        if (trend > 5) return <TrendingUp className="w-4 h-4 text-green-500" />;
        if (trend < -5) return <TrendingDown className="w-4 h-4 text-red-500" />;
        return <Minus className="w-4 h-4 text-gray-400" />;
    };

    return (
        <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 1.0 }}
            className={`
        rounded-xl shadow-sm p-[1px]
        ${isDarkMode ? 'bg-gradient-to-br from-green-500 to-emerald-600' : 'bg-transparent border border-gray-200'}
      `}
        >
            <div className={`p-4 rounded-xl h-full ${isDarkMode ? 'bg-slate-900' : 'bg-white'}`}>
                <h3 className={`text-lg font-semibold mb-4 flex items-center ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    <TrendingUp className="h-5 w-5 mr-2 text-green-600" />
                    Top Producers
                    {useCalculatedValues && (
                        <span className={`ml-2 text-xs font-normal px-2 py-0.5 rounded-full ${isDarkMode ? 'bg-emerald-900/50 text-emerald-400' : 'bg-emerald-100 text-emerald-700'}`}>
                            Calculated
                        </span>
                    )}
                </h3>
                <div className="space-y-3">
                    {sortedWells.map((well, index) => (
                        <motion.div
                            key={well.id}
                            initial={{ opacity: 0, x: -10 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: 1.1 + index * 0.1 }}
                            whileHover={{ scale: 1.02 }}
                            onClick={() => onSelectWell(well)}
                            className={`
                flex items-center justify-between p-3 rounded-lg cursor-pointer transition-colors
                ${isDarkMode ? 'bg-slate-800 hover:bg-slate-700' : 'bg-green-50 hover:bg-green-100'}
              `}
                        >
                            <div className="flex items-center space-x-3">
                                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${isDarkMode ? 'bg-card' : 'bg-green-100'}`}>
                                    <span className={`text-sm font-bold ${isDarkMode ? 'text-green-400' : 'text-green-700'}`}>{index + 1}</span>
                                </div>
                                <div>
                                    <p className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{well.name}</p>
                                    <div className="flex items-center gap-2">
                                        <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                                            {well.location || 'No location'}
                                        </p>
                                        {well.waterCut > 40 && (
                                            <span className={`flex items-center gap-1 text-xs ${well.waterCut > 60 ? 'text-red-500' : 'text-amber-500'}`}>
                                                <Droplets className="w-3 h-3" />
                                                {well.waterCut.toFixed(0)}%
                                            </span>
                                        )}
                                    </div>
                                </div>
                            </div>
                            <div className="text-right">
                                <div className="flex items-center gap-2 justify-end">
                                    {getTrendIcon(well.trend)}
                                    <p className={`text-lg font-bold ${isDarkMode ? 'text-green-400' : 'text-green-700'}`}>
                                        {well.displayProduction.toFixed(1)} BBL
                                    </p>
                                </div>
                                {well.trend !== undefined && (
                                    <p className={`text-xs ${well.trend >= 0 ? 'text-green-600' : 'text-red-500'}`}>
                                        {well.trend >= 0 ? '+' : ''}{well.trend.toFixed(1)}% vs yesterday
                                    </p>
                                )}
                            </div>
                        </motion.div>
                    ))}
                    {sortedWells.length === 0 && (
                        <div className={`text-center py-4 ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                            <AlertCircle className="w-8 h-8 mx-auto mb-2 opacity-50" />
                            <p>No production data available</p>
                            <p className="text-xs mt-1">Enter daily readings to see top producers</p>
                        </div>
                    )}
                </div>
            </div>
        </motion.div>
    );
}
